import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useBrowserWalletStore } from '@/features/browserWallet/store';
import { useCustodialJobs } from '@/features/custodial/hooks/useCustodialJobs';
import { hasUsableWalletAuthority } from '@/features/custodial/hooks/useCustodialPanelState';
import { phaseLabel } from '@/features/custodial/operationPhase';
// the store module only: the flow barrel carries the sign-out dialog.
import { useFlowStore } from '@/features/flow/store';
import {
    isFundAuthorizationUnavailableError,
    runWithFundAuthorization,
} from '@/features/fundAuthorization';
import { useUntrackedExposure } from '@/features/trading/hooks/useUntrackedExposure';
import {
    selectOpenPositionCount,
    useTradingStore,
} from '@/features/trading/store';
import { useTradingSessionStore } from '@/features/tradingSession/store';
import {
    useAnchorChain,
    useAuthedApi,
    useFactionsAuthedApi,
    useWallet,
} from '@/features/wallet';
import {
    fetchSpeedJob,
    migrateStartPayloadSchema,
    startMigrate,
} from '@/lib/api/speed';
import type { SpeedJobResponse } from '@/lib/api/speed';
import { describeErrorText, isApiError, isUserRejection } from '@/lib/errors';
import { queryKeys } from '@/lib/query';

import { MIGRATION_COPY, MIGRATION_ERROR_COPY } from './copy';
import { useMigrateQuote } from './useMigrateQuote';

/**
 * - `idle`: ready to start.
 * - `blocked`: an open position, a resting order or untracked exposure is in
 *   the way (the backend refuses the sweep while any exists).
 * - `needsWallet`: no connected owner wallet with a wallet (eip712) window;
 *   the proof is signed by that wallet, exactly as a V1 withdrawal.
 * - `signing`: the fund-authorization round trip and its wallet prompt.
 * - `running`: the `migrate` job is moving the money.
 * - `done` / `failed`: terminal for this run.
 */
export type MigrationStatus =
    | 'idle'
    | 'blocked'
    | 'needsWallet'
    | 'signing'
    | 'running'
    | 'done'
    | 'failed';

type RunPhase = 'idle' | 'signing' | 'running' | 'done' | 'failed';

export type MigrationChain = 'HyperCore' | 'HyperEVM';

export interface MigrationStage {
    /** The backend step that names this row (its label comes from `operationPhase`). */
    step: string;
    label: string;
    chain: MigrationChain;
}

/**
 * The rows the dialog lists while a migration runs. `migrate.preparing`
 * (and a job that is still `queued`) counts as the first row: it is the
 * bookkeeping that leads into the sweep, not a stage of its own.
 */
export const MIGRATION_STAGES: readonly MigrationStage[] = (
    [
        ['migrate.hypercoreSweep.waiting', 'HyperCore'],
        ['migrate.adapterFlush.waiting', 'HyperEVM'],
        ['migrate.transfer.waiting', 'HyperEVM'],
        ['migrate.gas.waiting', 'HyperEVM'],
    ] as const
).map(([step, chain]) => ({
    step,
    chain,
    label: phaseLabel(step, 'migrate')?.label ?? step,
}));

const STAGE_OF_STEP: Readonly<Record<string, number>> = {
    queued: 0,
    'migrate.preparing': 0,
    'migrate.hypercoreSweep.waiting': 0,
    'migrate.adapterFlush.waiting': 1,
    'migrate.transfer.waiting': 2,
    'migrate.gas.waiting': 3,
    settled: MIGRATION_STAGES.length,
};

/** The row a backend step belongs to, or null for a step that names none (`retrying`). */
export function stageOfStep(step: string | null | undefined): number | null {
    if (!step) return null;
    return STAGE_OF_STEP[step] ?? null;
}

interface MigrationTimings {
    pollIntervalMs: number;
    deadlineMs: number;
}

/** Poll the job every 3 s for up to 10 minutes, exactly like a V1 withdrawal. */
const DEFAULT_TIMINGS: MigrationTimings = {
    pollIntervalMs: 3_000,
    deadlineMs: 10 * 60_000,
};

let timings: MigrationTimings = DEFAULT_TIMINGS;

/** Test seam: shorten the poll and the deadline; call with nothing to restore. */
export function setMigrationTimings(
    overrides?: Partial<MigrationTimings>,
): void {
    timings = overrides
        ? { ...DEFAULT_TIMINGS, ...overrides }
        : DEFAULT_TIMINGS;
}

const sleep = async (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms));

class MigrationDeadlineError extends Error {
    constructor() {
        super(MIGRATION_COPY.deadline);
        this.name = 'MigrationDeadlineError';
    }
}

interface RunState {
    phase: RunPhase;
    jobId: string | null;
    step: string | null;
    /** The furthest row reached; `MIGRATION_STAGES.length` once settled. */
    stage: number;
    error: string | null;
    /** What the job says it moves (USDC base units), from its own `response.amount`. */
    requestedRaw: bigint | null;
    /** What landed (USDC base units), from the settled job's payload. */
    migratedRaw: bigint | null;
    /** The address the job paid. */
    paidTo: string | null;
    /** The job could not add HYPE for gas (a note, never a failure). */
    gasMissing: boolean;
}

const INITIAL_RUN: RunState = {
    phase: 'idle',
    jobId: null,
    step: null,
    stage: 0,
    error: null,
    requestedRaw: null,
    migratedRaw: null,
    paidTo: null,
    gasMissing: false,
};

function errorCodeOf(error: unknown): string | null {
    if (isApiError(error)) {
        const code = error.body?.code;
        return typeof code === 'string' && code ? code : null;
    }
    if (typeof error === 'object' && error !== null && 'code' in error) {
        const code = (error as { code?: unknown }).code;
        return typeof code === 'string' && code ? code : null;
    }
    return null;
}

/** What the user reads for a failed migration: never the raw error. */
export function migrationErrorText(error: unknown): string {
    if (error instanceof MigrationDeadlineError) return MIGRATION_COPY.deadline;
    if (isUserRejection(error)) return MIGRATION_COPY.cancelled;
    if (isFundAuthorizationUnavailableError(error)) {
        return MIGRATION_COPY.wrongWallet;
    }
    const code = errorCodeOf(error);
    const mapped = code ? MIGRATION_ERROR_COPY[code] : undefined;
    if (mapped) return mapped;
    return describeErrorText(error, { operation: 'Migration' });
}

/** A failed job as a throwable the presenter can read: its sentence and its code. */
function jobFailure(job: SpeedJobResponse): Error {
    const error = new Error(job.error || MIGRATION_COPY.jobFailed);
    if (job.code) Object.assign(error, { code: job.code });
    return error;
}

/** A job's `amount` (USDC base units, 6dp), or null when it states none. */
function baseUnitsOf(raw: unknown): bigint | null {
    return typeof raw === 'string' && /^\d+$/u.test(raw) ? BigInt(raw) : null;
}

/**
 * The V1 to V2 migration (plan 2026-10-04-v1-to-v2-migration §2): the V1
 * custodial balance comes back from HyperCore and is paid to the user's
 * Snappy Wallet on HyperEVM by a `migrate` job, authorized by ONE EIP-712
 * fund proof from the connected owner wallet whose payout descriptor must
 * name the destination shown in the dialog.
 *
 * What moves is the migrate quote (`useMigrateQuote`), not the withdrawable
 * balance; the start request carries no `amount`, so the server moves
 * everything migratable when it runs, and the quote shown in the dialog is
 * the most the fund proof may name.
 *
 * Mounted only while the dialog is open (its state starts over on every
 * open, and the quote and the balance are re-read on every open); an active
 * `migrate` job found in the jobs list is picked up and followed instead of
 * starting a second one.
 */
export function useMigration() {
    const queryClient = useQueryClient();
    const authedApi = useAuthedApi();
    // job status reads come off the factions speed facet, like every V1 job's
    // status read; the start call goes to MoonHub.API.
    const factionsApi = useFactionsAuthedApi();
    const { address } = useWallet();
    const { anchorChainId } = useAnchorChain();
    const quote = useMigrateQuote();

    // ── gates ───────────────────────────────────────────────────────────
    const openPositions = useTradingStore(selectOpenPositionCount);
    const restingOrders = useTradingStore(
        (s) =>
            s.runtimeMode === 'real' &&
            Object.values(s.positionsByCoin).some(
                (slot) => slot.status === 'resting',
            ),
    );
    const exposure = useUntrackedExposure();
    const untracked =
        (exposure.data?.positions.length ?? 0) > 0 ||
        (exposure.data?.orders.length ?? 0) > 0;
    const blocked = openPositions > 0 || restingOrders || untracked;

    // subscribed so the gate re-reads when the wallet window or the wallet
    // connection changes; the decision itself is the custodial panel's.
    useTradingSessionStore((s) => s.status);
    useTradingSessionStore((s) => s.clockNowMs);
    const needsWallet =
        !hasUsableWalletAuthority() || !address || !authedApi || !factionsApi;

    // ── what moves, and where to ───────────────────────────────────────
    const v2Address = useFlowStore((s) => s.availability?.v2Address ?? null);
    const heldAddress = useBrowserWalletStore((s) => s.address);
    // the held key is the wallet V2 runs on here, so it wins when it is the
    // same backup (same address, its own checksum).
    const destination =
        heldAddress &&
        v2Address &&
        heldAddress.toLowerCase() === v2Address.toLowerCase()
            ? heldAddress
            : v2Address;
    // what the quote says moves; null until it has been read.
    const amountRaw = quote.migratableRaw;
    const reserveStays = quote.coreUsd !== null && quote.coreUsd > 0;

    const canStart =
        !blocked &&
        !needsWallet &&
        destination !== null &&
        quote.hasWallet &&
        amountRaw !== null &&
        amountRaw > 0n;

    // ── the run ────────────────────────────────────────────────────────
    const [run, setRun] = useState<RunState>(INITIAL_RUN);
    const lockRef = useRef(false);
    const aliveRef = useRef(true);
    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
        };
    }, []);

    const refreshBalances = useCallback(() => {
        void queryClient.invalidateQueries({
            queryKey: queryKeys.speed.balance(),
        });
        void queryClient.invalidateQueries({
            queryKey: queryKeys.speed.jobs(),
        });
        void queryClient.invalidateQueries({
            queryKey: queryKeys.speed.migrateQuote(),
        });
    }, [queryClient]);

    // the dialog just opened: what it shows is read again, not taken from
    // whatever the header last polled.
    useEffect(() => {
        refreshBalances();
    }, [refreshBalances]);

    const fail = useCallback(
        (error: unknown) => {
            console.error('[Migration] failed:', error);
            // a migration can fail after money already left HyperCore, so the
            // balance is re-read either way.
            refreshBalances();
            if (!aliveRef.current) return;
            setRun((prev) => ({
                ...prev,
                phase: 'failed',
                error: migrationErrorText(error),
            }));
        },
        [refreshBalances],
    );

    const track = useCallback(
        async (initial: SpeedJobResponse, requested: { to: string | null }) => {
            if (!factionsApi) throw new Error(MIGRATION_COPY.signIn);
            const deadline = Date.now() + timings.deadlineMs;
            let job = initial;
            for (;;) {
                if (!aliveRef.current) return;
                const current = job;
                setRun((prev) => ({
                    ...prev,
                    phase: 'running',
                    jobId: current.jobId,
                    requestedRaw:
                        baseUnitsOf(current.response?.amount) ??
                        prev.requestedRaw,
                    step: current.step,
                    // forward only: a step that names no row (`retrying`)
                    // keeps the row it interrupted.
                    stage: Math.max(prev.stage, stageOfStep(current.step) ?? 0),
                }));

                if (current.status === 'settled') {
                    const payload = migrateStartPayloadSchema.safeParse(
                        current.response,
                    );
                    setRun((prev) => ({
                        ...prev,
                        phase: 'done',
                        stage: MIGRATION_STAGES.length,
                        migratedRaw: payload.success
                            ? baseUnitsOf(payload.data.amount)
                            : null,
                        paidTo: payload.success
                            ? payload.data.destinationAddress
                            : requested.to,
                        gasMissing:
                            payload.success &&
                            payload.data.gas?.sent === false &&
                            !!payload.data.gas.note,
                    }));
                    refreshBalances();
                    return;
                }
                if (
                    current.status === 'failed' ||
                    current.status === 'refunded'
                ) {
                    throw jobFailure(current);
                }
                if (Date.now() >= deadline) throw new MigrationDeadlineError();

                await sleep(timings.pollIntervalMs);
                if (!aliveRef.current) return;
                job = await fetchSpeedJob(factionsApi, initial.jobId);
            }
        },
        [factionsApi, refreshBalances],
    );

    const follow = useCallback(
        async (job: SpeedJobResponse) => {
            if (lockRef.current) return;
            lockRef.current = true;
            setRun({
                ...INITIAL_RUN,
                phase: 'running',
                jobId: job.jobId,
                requestedRaw: baseUnitsOf(job.response?.amount),
            });
            try {
                await track(job, { to: destination });
            } catch (error) {
                fail(error);
            } finally {
                lockRef.current = false;
            }
        },
        [destination, fail, track],
    );

    const start = useCallback(async () => {
        if (lockRef.current) return;
        if (!canStart || !authedApi || !destination || amountRaw === null) {
            // a gate closed since the button was drawn: show it, start nothing.
            setRun(INITIAL_RUN);
            return;
        }
        lockRef.current = true;
        setRun({ ...INITIAL_RUN, phase: 'signing' });
        try {
            const job = await runWithFundAuthorization(
                // no `amount`: the server moves everything migratable when it
                // runs, which a quote read a few seconds ago cannot pin.
                async (fundAuthorizations) =>
                    startMigrate(authedApi, {
                        destinationAddress: destination,
                        anchorChainId,
                        fundAuthorizations,
                    }),
                {
                    flow: 'migration',
                    migrationDestination: destination,
                    connectedAddress: address,
                    // the ceiling the proof may name: the figure the dialog
                    // showed. A server asking for more is refused unsigned.
                    amount: amountRaw,
                    anchorChainId,
                },
            );
            await track(job, { to: destination });
        } catch (error) {
            fail(error);
        } finally {
            lockRef.current = false;
        }
    }, [
        address,
        amountRaw,
        anchorChainId,
        authedApi,
        canStart,
        destination,
        fail,
        track,
    ]);

    // a migration already running (this tab reloaded, or another tab started
    // it) is followed rather than started twice: the backend would refuse the
    // second one anyway.
    const { activeJobs } = useCustodialJobs(true);
    const activeMigration =
        activeJobs.find((job) => job.kind === 'migrate') ?? null;
    const followedRef = useRef<string | null>(null);
    useEffect(() => {
        if (!activeMigration || run.phase !== 'idle' || lockRef.current) return;
        if (followedRef.current === activeMigration.jobId) return;
        followedRef.current = activeMigration.jobId;
        void follow(activeMigration);
    }, [activeMigration, follow, run.phase]);

    const retry = useCallback(async () => {
        if (activeMigration) {
            followedRef.current = activeMigration.jobId;
            await follow(activeMigration);
            return;
        }
        await start();
    }, [activeMigration, follow, start]);

    let status: MigrationStatus = run.phase;
    if (run.phase === 'idle') {
        if (blocked) status = 'blocked';
        else if (needsWallet) status = 'needsWallet';
    }

    return {
        status,
        /** What a migration would move (USDC base units), from the migrate quote; null until read. */
        amountRaw,
        /** The quote is being read for the first time. */
        quoteLoading: quote.isLoading,
        /** HyperCore holds part of the V1 balance, so its 1.01 USDC reserve stays behind. */
        reserveStays,
        /** The Snappy Wallet the migration pays, or null when V2 has none yet. */
        destination,
        canStart,
        start,
        retry,
        step: run.step,
        stage: run.stage,
        error: run.error,
        /** What moved (USDC base units), from the job's own figures, never the quote. */
        migratedRaw: run.migratedRaw ?? run.requestedRaw,
        paidTo: run.paidTo ?? destination,
        gasMissing: run.gasMissing,
    };
}
